import React from "react";
//#region redux
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { ThunkDispatch } from "redux-thunk";
import { closeDialog, displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WidgetBaseProps } from "models/propTypes/commonPropTypes";
import { DisplayType } from "models/enums/style";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, FAQConstants } from "utils/constants";
//#endregion
//#region components
import { ButtonBase } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Icon, ActionLinkButton } from "components/common";
import { useSupportTicketCreateDialog, useSupportTicketCreateDialogWithFAQ } from "components/support";
//#endregion
import { useSupportTeamContent } from "serviceHooks/useSupportTeamContent";
import { useMeetergo } from "serviceHooks/useMeetergo";
import useSettingsService from "serviceHooks/useSettingsService";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { useNavigate } from "react-router-dom";

const SupportTeamWidget: React.FC<WidgetBaseProps> = (props) => {
  const companyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const companyName = useSelector((state: AppState) => state.auth.selectedCompany.name);
  const userName = useSelector((state: AppState) => state.auth.loggedInUser?.name);
  const userEmail = useSelector((state: AppState) => state.auth.loggedInUser?.mail);
  const { data, loading } = useSupportTeamContent(companyId);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const navigate = useNavigate();
  const { openPopup: openBooking } = useMeetergo();
  const { getProfileSettingsSilent } = useSettingsService();

  const { getDialog: getSupportTicketCreateDialog } = useSupportTicketCreateDialog();
  const supportTicketDialogWithFAQ = useSupportTicketCreateDialogWithFAQ(undefined, () => dispatch(closeDialog()));

  const hasTeam = data?.team && data.team.members.length > 0;
  const isExternal = !!data?.externalAgencyId;
  const hasContent = hasTeam || isExternal;

  React.useEffect(() => {
    props.onVisibilityChange?.(loading || !hasContent);
  }, [loading, hasContent, props.onVisibilityChange]);

  if (loading || !hasContent) return null;

  const { team, content, showPhone } = data!;

  /** CMS content with i18n fallback */
  const t = (field: keyof NonNullable<typeof content>, fallbackKey: string) =>
    content?.[field] || LocalizationHelperInstance.getFormattedMessage(fallbackKey);

  const onFAQClick = () => {
    Sentry.captureMessage("Support team: FAQ clicked", { level: "info", tags: { source: "dashboard-widget" } });
    window.open(FAQConstants.FAQUrl, "_blank", "noopener,noreferrer");
  };

  const onTeamClick = () => {
    Sentry.captureMessage("Support team: Träffa teamet clicked", {
      level: "info",
      tags: { source: "dashboard-widget" },
    });
    navigate(UrlHelper.formatPath("/Support/Team"));
  };

  const onMessageClick = () => {
    Sentry.captureMessage("Support team: Skicka meddelande clicked", {
      level: "info",
      tags: { source: "dashboard-widget", variant: isExternal ? "external" : "wint-team" },
    });
    const skipFaq = isExternal ? data?.agencySkipFaqStep : data?.team?.skipFaqStep;
    if (skipFaq) {
      dispatch(displayDialog(getSupportTicketCreateDialog(undefined, undefined, undefined, true)));
    } else {
      dispatch(displayDialog(supportTicketDialogWithFAQ));
    }
  };

  if (isExternal) {
    const advisorName = data!.advisorName || "";

    return (
      <div className={`${cssExports.supportTeamWidgetContainer} ${cssExports.supportTeamWidgetExternal}`}>
        <div className={cssExports.supportTeamHeader}>
          <div className={cssExports.supportTeamHeaderText}>
            <div className={cssExports.supportTeamTitle}>
              {t("externalHeaderTitle", TranslationKeys.Common.GetPersonalAdvice)}
            </div>
            <div className={cssExports.supportTeamDescription}>
              {data!.agencyDescription ||
                t("externalFallbackDescription", TranslationKeys.Common.ExternalAdvisorDescription)}
            </div>
          </div>
          {data!.advisorImageUrl && (
            <div className={cssExports.supportTeamExternalAvatar}>
              <img src={data!.advisorImageUrl} alt={advisorName} />
            </div>
          )}
        </div>

        {data!.advisorBookingUrl && (
          <ActionLinkButton
            className={clsx(cssExports.supportTeamLink, cssExports.seeMoreLink, cssExports.animatedLink)}
            onClick={async () => {
              Sentry.captureMessage("Support team: Boka möte clicked", {
                level: "info",
                tags: { source: "dashboard-widget", variant: "external" },
              });
              // Build the prefill payload — see `MeetergoPrefill` for the
              // contract. We pass every predefined slug the SDK supports
              // plus `bolagsnamn` (the advisor's custom slug for the
              // company field); the SDK ignores keys that don't match a
              // field on the advisor's actual form.
              // Split the name on first whitespace for the firstname/lastname slots.
              const trimmedName = (userName || "").trim();
              const whitespace = /\s/.exec(trimmedName);
              const firstname = whitespace ? trimmedName.slice(0, whitespace.index) : trimmedName;
              const lastname = whitespace ? trimmedName.slice(whitespace.index + whitespace[0].length).trim() : "";
              const company = companyName || "";
              // Phone isn't in Redux — fetch profile settings on click so we
              // always have the latest value when the popup opens. Adds one
              // network roundtrip before the Meetergo modal shows; worth it
              // to guarantee the phone field is populated instead of racing
              // a background prefetch. Best-effort on failure — the silent
              // variant keeps the global error toast from firing when the
              // user just wanted to book a meeting.
              const phone = await getProfileSettingsSilent()
                .then((p) => p?.phone ?? "")
                .catch(() => "");
              openBooking(data!.advisorBookingUrl!, {
                // Predefined slugs
                firstname,
                lastname,
                fullname: trimmedName,
                email: userEmail || "",
                phone,
                // Custom slug — Meetergo has no predefined "company" field,
                // so the advisor added one under the Swedish id `bolagsnamn`.
                bolagsnamn: company,
                // Custom slug — Company id (selectedCompany.id). String() because prefill values must be strings.
                companyid: String(companyId),
              });
            }}
            endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={DisplayType.Info} />}
          >
            {t("bookMeetingLink", TranslationKeys.Common.BookMeeting)}
          </ActionLinkButton>
        )}

        <div className={cssExports.supportTeamActions}>
          <ButtonBase component="div" onClick={onFAQClick}>
            <div className={cssExports.supportTeamActionCard}>
              <div className={cssExports.supportTeamActionIcon}>
                <Icon icon={IconDefinitionConstants.WQuestionCircle} displayType={DisplayType.Info} />
              </div>
              <div className={cssExports.supportTeamActionContent}>
                <div className={cssExports.supportTeamActionTitle}>{t("faqTitle", TranslationKeys.Common.Faq)}</div>
                <div className={cssExports.supportTeamActionSubtitle}>
                  {t("faqSubtitle", TranslationKeys.Common.FaqSubtitle)}
                </div>
              </div>
            </div>
          </ButtonBase>

          <ButtonBase component="div" onClick={onMessageClick}>
            <div className={cssExports.supportTeamActionCard}>
              <div className={cssExports.supportTeamActionIcon}>
                <Icon icon={IconDefinitionConstants.WCommentDots} displayType={DisplayType.Info} />
              </div>
              <div className={cssExports.supportTeamActionContent}>
                <div className={cssExports.supportTeamActionTitle}>
                  {t("sendMessageTitle", TranslationKeys.Common.SendMessage)}
                </div>
                <div className={cssExports.supportTeamActionSubtitle}>
                  {t("sendMessageSubtitle", TranslationKeys.Common.SendMessageSubtitle)}
                </div>
              </div>
            </div>
          </ButtonBase>
        </div>
      </div>
    );
  }

  return (
    <div className={cssExports.supportTeamWidgetContainer}>
      {/* Header: Banner text + team avatars */}
      <div className={cssExports.supportTeamHeader}>
        <div className={cssExports.supportTeamHeaderText}>
          <div className={cssExports.supportTeamTitle}>{t("bannerTitle", TranslationKeys.Common.HelpTitle)}</div>
          <div className={cssExports.supportTeamDescription}>
            {t("bannerDescription", TranslationKeys.Common.HelpDescription)}
          </div>
        </div>
        <div className={cssExports.supportTeamAvatarGroup}>
          {team!.members.slice(0, 3).map((member) => (
            <div key={member.imageUrl} className={cssExports.supportTeamAvatar}>
              {member.imageUrl ? (
                <img src={member.imageUrl} alt={member.name} />
              ) : (
                <div className={cssExports.supportTeamAvatarPlaceholder}>{member.name.charAt(0)}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Team link */}
      <ActionLinkButton
        className={clsx(cssExports.supportTeamLink, cssExports.seeMoreLink, cssExports.animatedLink)}
        onClick={onTeamClick}
        endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={DisplayType.Info} />}
      >
        {t("meetTheTeamLink", TranslationKeys.Common.MeetTheTeam)}
      </ActionLinkButton>

      {/* Action cards */}
      <div className={cssExports.supportTeamActions}>
        <ButtonBase component="div" onClick={onFAQClick}>
          <div className={cssExports.supportTeamActionCard}>
            <div className={cssExports.supportTeamActionIcon}>
              <Icon icon={IconDefinitionConstants.WQuestionCircle} displayType={DisplayType.Info} />
            </div>
            <div className={cssExports.supportTeamActionContent}>
              <div className={cssExports.supportTeamActionTitle}>{t("faqTitle", TranslationKeys.Common.Faq)}</div>
              <div className={cssExports.supportTeamActionSubtitle}>
                {t("faqSubtitle", TranslationKeys.Common.FaqSubtitle)}
              </div>
            </div>
          </div>
        </ButtonBase>

        <ButtonBase component="div" onClick={onMessageClick}>
          <div className={cssExports.supportTeamActionCard}>
            <div className={cssExports.supportTeamActionIcon}>
              <Icon icon={IconDefinitionConstants.WCommentDots} displayType={DisplayType.Info} />
            </div>
            <div className={cssExports.supportTeamActionContent}>
              <div className={cssExports.supportTeamActionTitle}>
                {t("sendMessageTitle", TranslationKeys.Common.SendMessage)}
              </div>
              <div className={cssExports.supportTeamActionSubtitle}>
                {t("sendMessageSubtitle", TranslationKeys.Common.SendMessageSubtitle)}
              </div>
            </div>
          </div>
        </ButtonBase>

        {showPhone && (
          <div className={cssExports.supportTeamPhoneLink}>
            <div className={cssExports.supportTeamActionCard}>
              <div className={cssExports.supportTeamActionIcon}>
                <Icon icon={IconDefinitionConstants.WSupportCenter} displayType={DisplayType.Info} />
              </div>
              <div className={cssExports.supportTeamActionContent}>
                <div className={cssExports.supportTeamActionTitle}>
                  {t("callSupportTitle", TranslationKeys.Common.CallSupport)}
                </div>
                <div className={cssExports.supportTeamActionSubtitle}>
                  {t("callSupportSubtitle", TranslationKeys.Common.CallSupportSubtitle)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTeamWidget;
